import { Component, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { FetchProjectsAction } from '../../../../admin/project-admin/actions/fetch-projects.action';
import { Observable } from 'rxjs';
import { ProjectAdminState } from '../../../../admin/project-admin/project-admin.state';
import {
  Project,
  TimeEntry,
  User,
} from '../../../../../../../../common/interfaces/prisma.binding';
import { FetchTimeEntriesAction } from '../../../../admin/project-admin/actions/fetch-time-entries.action';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { AuthState } from '../../../../shared/state/auth.state';
import { UserState } from '../../../../admin/user/user.state';
import { FetchUserAction } from '../../../../admin/user/actions/fetch-user.action';
import { CreateProjectAction } from '../../../../admin/project-admin/actions/create-project.action';
import { DeleteProjectAction } from '../../../../admin/project-admin/actions/delete-project.action';
import { UpdateProjectAction } from '../../../../admin/project-admin/actions/update-project.action';

@Component({
  selector: 'tt-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.css'],
})
export class ProjectsComponent implements OnInit {
  @Select(ProjectAdminState.projects) projects$: Observable<Project[]>;
  @Select(ProjectAdminState.timeEntries) timeEntries$: Observable<TimeEntry[]>;
  @Select(AuthState.isAdmin) isAdmin$: Observable<boolean>;
  @Select(UserState.users) users$: Observable<User[]>;

  projectForm: FormGroup;
  projectDataSource: MatTableDataSource<Project>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumnsAdmin: string[] = [
    'projectNumber',
    'projectDescription',
    'isGlobal',
    'action',
  ];

  displayedColumns: string[] = [
    'projectNumber',
    'projectDescription',
    'action',
  ];

  isAdmin: boolean;
  editMode = false;
  private _currentUser: User;

  constructor(
    private readonly store: Store,
    private readonly formBuilder: FormBuilder,
    public readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isAdmin$.subscribe((admin) => {
      this.isAdmin = admin;
      if (this.isAdmin) {
        this.store.dispatch(new FetchUserAction());
      }
    });
    this.store.dispatch([
      new FetchTimeEntriesAction(),
      new FetchProjectsAction(),
    ]);

    this.projects$.subscribe((projects) => {
      this.projectDataSource = new MatTableDataSource(projects);
      this.projectDataSource.paginator = this.paginator;
      this.projectDataSource.sort = this.sort;
    });
    this.projectForm = this.formBuilder.group({
      projectNumber: ['', Validators.required],
      projectDescription: [''],
      isGlobal: [false],
      id: [],
    });

    this._currentUser = this.store.selectSnapshot(
      AuthState.userDetails,
    ) as User;
  }

  timeChanges() {
    const bookedTime = this.projectForm.get('bookedTime').value;
    if (bookedTime.indexOf(':') === -1) {
      this.projectForm.patchValue({ bookedTime: bookedTime + ':00' });
    }
  }

  async createProject() {
    const project = {
      projectDescription: this.projectForm.get('projectDescription').value,
      projectNumber: this.projectForm.get('projectNumber').value,
      isGlobal: this.projectForm.get('isGlobal').value,
      userId: this._currentUser.id,
    };
    await this.store.dispatch(new CreateProjectAction(project)).toPromise();
    this.projectForm.reset();
  }

  createBookedTime(bookedTime: string): number {
    const timeParts = bookedTime.split(':');
    return parseInt(timeParts[0], 10) * 3600 + parseInt(timeParts[1], 10) * 60;
  }

  deleteProject(projectId: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: 'Dieses Projekt wirklich löschen?',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store
          .dispatch(new DeleteProjectAction(projectId))
          .subscribe(() => {
            this.store.dispatch(new FetchProjectsAction());
          });
      }
    });
  }

  editProject(projectId: string) {
    this.projects$.subscribe((projects) => {
      this.projectForm.patchValue(
        projects.find((project) => project.id === projectId),
      );
      this.editMode = true;
    });
  }

  async updateProject() {
    const project = {
      projectDescription: this.projectForm.get('projectDescription').value,
      projectNumber: this.projectForm.get('projectNumber').value,
      isGlobal: this.projectForm.get('isGlobal').value,
      userId: this._currentUser.id,
      id: this.projectForm.get('id').value,
    };
    await this.store.dispatch(new UpdateProjectAction(project)).toPromise();
    this.projectForm.reset();
    this.editMode = false;
    this.store.dispatch(new FetchProjectsAction());
  }
}
